// API bindings: sinks (Auto generated)

"use strict";

import { RequestErrorHandler, RequestParams, CommonAuthenticatedErrorHandler } from "@asanrom/request-browser";
import { getApiUrl, generateURIQuery } from "./utils";
import { DataSinkConnectionType, DataSinkList, CreateSinkResponse, CreateSinkBody, DataSinkFullInformation, RenameDataSinkBody, ModifyFieldsDataSinkBody, ModifySinkConfigResponse, ModifyConfigDataSinkBody, DataSinkTestConnectivityResponse, DataSinkGetFieldsResponse, DataSinkExistsResponse } from "./definitions";

export class ApiSinks {
    /**
     * Method: GET
     * Path: /sink-types
     * Gets list of data sink connection types
     * Requires permission: sources.read
     * @param queryParams Query parameters
     * @returns The request parameters
     */
    public static ListSinkTypes(queryParams: ListSinkTypesQueryParameters): RequestParams<DataSinkConnectionType[], ListSinkTypesErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/sink-types` + generateURIQuery(queryParams)),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /sinks
     * Gets list of data sinks
     * Requires permission: sinks.read
     * @param queryParams Query parameters
     * @returns The request parameters
     */
    public static ListSinks(queryParams: ListSinksQueryParameters): RequestParams<DataSinkList, ListSinksErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/sinks` + generateURIQuery(queryParams)),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /sinks
     * Creates new data sink
     * Requires permission: sinks.write
     * @param body Body parameters
     * @returns The request parameters
     */
    public static CreateSink(body: CreateSinkBody): RequestParams<CreateSinkResponse, CreateSinkErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/sinks`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_FIELDS", handler.badRequestInvalidFields)
                    .add(400, "INVALID_CONNECTION_CONFIG", handler.badRequestInvalidConnectionConfig)
                    .add(400, "INVALID_CONNECTION_TYPE", handler.badRequestInvalidConnectionType)
                    .add(400, "INVALID_DATA_FLOW_TYPE", handler.badRequestInvalidDataFlowType)
                    .add(400, "INVALID_NAME", handler.badRequestInvalidName)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /sinks/{id}
     * Gets sink information
     * Requires permission: sinks.read
     * @param id Data sink ID
     * @returns The request parameters
     */
    public static GetSink(id: string): RequestParams<DataSinkFullInformation, GetSinkErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/sinks/${encodeURIComponent(id)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: DELETE
     * Path: /sinks/{id}
     * Deletes data sink
     * Requires permission: sinks.write
     * @param id Data sink ID
     * @returns The request parameters
     */
    public static DeleteSink(id: string): RequestParams<void, DeleteSinkErrorHandler> {
        return {
            method: "DELETE",
            url: getApiUrl(`/sinks/${encodeURIComponent(id)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /sinks/{id}/name
     * Renames data sink
     * Requires permission: sinks.write
     * @param id Data sink ID
     * @param body Body parameters
     * @returns The request parameters
     */
    public static RenameSink(id: string, body: RenameDataSinkBody): RequestParams<void, RenameSinkErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/sinks/${encodeURIComponent(id)}/name`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_NAME", handler.badRequestInvalidName)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /sinks/{id}/fields
     * Modifies fields of data sink
     * Requires permission: sinks.write
     * @param id Data sink ID
     * @param body Body parameters
     * @returns The request parameters
     */
    public static ModifySinkFields(id: string, body: ModifyFieldsDataSinkBody): RequestParams<void, ModifySinkFieldsErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/sinks/${encodeURIComponent(id)}/fields`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_FIELDS", handler.badRequestInvalidFields)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /sinks/{id}/config
     * Modifies data sink configuration
     * Requires permission: sinks.write
     * @param id Data sink ID
     * @param body Body parameters
     * @returns The request parameters
     */
    public static ModifySinkConfig(id: string, body: ModifyConfigDataSinkBody): RequestParams<ModifySinkConfigResponse, ModifySinkConfigErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/sinks/${encodeURIComponent(id)}/config`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_CONNECTION_CONFIG", handler.badRequestInvalidConnectionConfig)
                    .add(400, "INVALID_CONNECTION_TYPE", handler.badRequestInvalidConnectionType)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /sinks_tools/test
     * Tests connectivity to a data sink
     * Requires permission: sinks.write
     * @param body Body parameters
     * @returns The request parameters
     */
    public static TestConnectivity(body: ModifyConfigDataSinkBody): RequestParams<DataSinkTestConnectivityResponse, TestConnectivityErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/sinks_tools/test`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_CONNECTION_CONFIG", handler.badRequestInvalidConnectionConfig)
                    .add(400, "INVALID_CONNECTION_TYPE", handler.badRequestInvalidConnectionType)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /sinks_tools/get_fields
     * Connects to the data sink and figures out the list of fields.
     * May return an empty list if the data sink cannot be reached.
     * Requires permission: sinks.write
     * @param body Body parameters
     * @returns The request parameters
     */
    public static GetFields(body: ModifyConfigDataSinkBody): RequestParams<DataSinkGetFieldsResponse, GetFieldsErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/sinks_tools/get_fields`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_CONNECTION_CONFIG", handler.badRequestInvalidConnectionConfig)
                    .add(400, "INVALID_CONNECTION_TYPE", handler.badRequestInvalidConnectionType)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /sinks/check-name/{name}
     * Gets sink information
     * Requires permission: sinks.read
     * @param name Data sink name
     * @returns The request parameters
     */
    public static GetSinkByName(name: string): RequestParams<DataSinkExistsResponse, GetSinkByNameErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/sinks/check-name/${encodeURIComponent(name)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }
}

/**
 * Error handler for ListSinkTypes
 */
export type ListSinkTypesErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Query parameters for ListSinkTypes
 */
export interface ListSinkTypesQueryParameters {
    /**
     * Language for the documentation fields (name, description, etc.)
     */
    lang?: string;
}

/**
 * Error handler for ListSinks
 */
export type ListSinksErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Query parameters for ListSinks
 */
export interface ListSinksQueryParameters {
    /**
     * Max number of items to fetch per page. Hard limit: 256
     */
    limit?: number;

    /**
     * Continuation token, to request more pages
     */
    continuation?: number;
}

/**
 * Error handler for CreateSink
 */
export type CreateSinkErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid name
     */
    badRequestInvalidName: () => void;

    /**
     * Invalid data flow type
     */
    badRequestInvalidDataFlowType: () => void;

    /**
     * Invalid or incompatible connection
     */
    badRequestInvalidConnectionType: () => void;

    /**
     * Invalid connection configuration
     */
    badRequestInvalidConnectionConfig: () => void;

    /**
     * Invalid fields definition
     */
    badRequestInvalidFields: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for GetSink
 */
export type GetSinkErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for DeleteSink
 */
export type DeleteSinkErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for RenameSink
 */
export type RenameSinkErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid name
     */
    badRequestInvalidName: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for ModifySinkFields
 */
export type ModifySinkFieldsErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid fields definition
     */
    badRequestInvalidFields: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for ModifySinkConfig
 */
export type ModifySinkConfigErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid or incompatible connection
     */
    badRequestInvalidConnectionType: () => void;

    /**
     * Invalid connection configuration
     */
    badRequestInvalidConnectionConfig: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for TestConnectivity
 */
export type TestConnectivityErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid or incompatible connection
     */
    badRequestInvalidConnectionType: () => void;

    /**
     * Invalid connection configuration
     */
    badRequestInvalidConnectionConfig: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for GetFields
 */
export type GetFieldsErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid or incompatible connection
     */
    badRequestInvalidConnectionType: () => void;

    /**
     * Invalid connection configuration
     */
    badRequestInvalidConnectionConfig: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for GetSinkByName
 */
export type GetSinkByNameErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

